<template>
  <div>
    <div class="helmi-card-title pb-4 pl-0">
      Ostotoimeksianto #{{ buyOrder.ordernumber }}
    </div>
    <v-card
      v-if="agent"
      outlined
      class="pa-4"
    >
      <h2 class="mt-3 mb-0">
        Tila: <span class="primary--text ml-1">{{ getStatus(buyOrder.status) }}</span>
      </h2>
      <h2 class="my-4">
        Julkinen teksti:
      </h2>
      <p class="" style="white-space: pre-wrap;">
        {{ buyOrder.description }}
      </p>
      <buy-order-edit
        :buy-order="buyOrder"
        :current-status="currentStatus"
      />
    </v-card>
    <h1 class="mb-5 mt-6">
      Ostotoimeksiantosopimus
    </h1>
    <h2>1. Toimeksiantajat</h2>
    <p
      v-for="(person, idx) of buyOrder.persons"
      :key="idx"
      class="mt-4"
    >
      1.{{ idx + 1 }}.1 Nimi: <span class="primary--text ml-1">{{ person.name }}</span><br>
      1.{{ idx + 1 }}.2 Sähköposti: <span class="primary--text ml-1">{{ person.email }}</span><br>
      1.{{ idx + 1 }}.3 Henkilötunnus: <span class="primary--text ml-1">{{ person.ssn }}</span><br>
      1.{{ idx + 1 }}.4 Puhelinnumero: <span class="primary--text ml-1">
        {{ person.phone }}</span><br>
      1.{{ idx + 1 }}.5 Osoite: <span class="primary--text ml-1">{{ person.address }}</span><br>
      1.{{ idx + 1 }}.6 Postinumero: <span class="primary--text ml-1">{{ person.zip }}</span><br>
      1.{{ idx + 1 }}.7 Kaupunki: <span class="primary--text ml-1">{{ person.city }}</span><br>
    </p>

    <h2 class="mt-4">
      2. Välitysliike
    </h2>
    <p v-if="buyOrder.company" class="mt-4">
      2.1 Nimi: <span class="primary--text ml-1">{{ buyOrder.company.nimi }}</span><br>
      2.2 Y-tunnus: <span class="primary--text ml-1">{{ buyOrder.company.ytunnus }}</span><br>
      2.3 Osoite: <span class="primary--text ml-1">{{ buyOrder.company.osoite }}</span><br>
      2.4 Toimeksiannon hoitaja: <span class="primary--text ml-1">
        {{ buyOrder.agent_details.nimi }}</span><br>
      2.5 Puhelin: <span class="primary--text ml-1">{{ buyOrder.agent_details.puhelin }}</span><br>
      2.6 Sähköposti: <span class="primary--text ml-1">{{ buyOrder.agent_details.email }}</span>
    </p>

    <h2 class="mt-4">
      3. Toimeksiannon kohde
    </h2>
    <p class="mt-4">
      3.1 Haluttu kohdetyyppi: <span class="primary--text ml-1">
        {{ buyOrder.readable_property_type }}</span><br>
      <template v-if="buyOrder.fields.condition">
        3.2 Kohteen kunto: <span class="primary--text ml-1">
          {{ buyOrder.fields.condition }}</span><br>
      </template>
      <template v-if="buyOrder.fields.size">
        3.3 Kohteen koko: <span class="primary--text ml-1">{{ buyOrder.fields.size }}</span><br>
      </template>
      <template v-if="buyOrder.fields.equipment">
        3.4 Kohteen varustetaso: <span class="primary--text ml-1">{{ buyOrder
          .fields.equipment }}</span><br>
      </template>
      <template v-if="buyOrder.fields.vacancy">
        3.5 Kohteen vapautuminen: <span class="primary--text ml-1">{{ buyOrder
          .fields.vacancy }}</span><br>
      </template>
      <template v-if="buyOrder.fields.paymentTerms">
        3.6 Maksuehdot: <span class="primary--text ml-1">
          {{ buyOrder.fields.paymentTerms }}</span><br>
      </template>
      <template v-if="buyOrder.fields.conditions">
        3.7 Kohteen muut ehdot ja erityistoiveet:
        <span class="primary--text ml-1">{{ buyOrder.fields.conditions }}</span><br>
      </template>
      <template v-if="buyOrder.fields.rooms">
        3.8 Kohteen huonelukumäärä:
        <span class="primary--text ml-1">{{ buyOrder.fields.rooms }}</span><br>
      </template>
      <template v-if="buyOrder.fields.price">
        3.9 Kohteen hinta:
        <span class="primary--text ml-1">{{ buyOrder.fields.price }}</span><br>
      </template>
    </p>

    <h2 class="mt-4">
      4. Voimassaoloaika
    </h2>
    <p class="mt-4">
      4.1 Toimeksiannon alku:
      <span class="primary--text ml-1">{{ buyOrder.fields.lengthPeriod !== 1 ?
        $options.filters.date(buyOrder.created) : '' }}</span><br>
      4.2 Toimeksiannon loppu:
      <span class="primary--text ml-1">{{ buyOrder.fields.lengthPeriod !== 1 ?
        $options.filters.date(buyOrder.ends) : '' }}</span><br>
      4.3 Toistaiseksi voimassa oleva alkaen:
      <span class="primary--text ml-1">{{ buyOrder.fields.lengthPeriod === 1 ?
        $options.filters.date(buyOrder.created) : '' }}</span><br>
      4.4 Toistaiseksi voimassa olevan toimeksiannon jakson pituus:
      <span class="primary--text ml-1">{{ buyOrder.fields.lengthPeriod === 1 ?
        buyOrder.fields.forNow : '' }}</span><br>
    </p>

    <h2 class="mt-4">
      5. Välityspalkkio
    </h2>
    <p class="mt-4">
      5.1 Välityspalkkio (kiinteä):
      <span class="primary--text ml-1">{{ buyOrder.brokerage | currency }}</span><br>
      5.2 Välityspalkkio (prosentti):
      <span class="primary--text ml-1">{{ buyOrder.fields
        .brokeragePercent | currency({ style: 'decimal' }) }} %</span><br>
      5.3 Muu palkkioperuste, toimeksiantajalle aiheutuvat muut kustannukset ja vähimmäispalkkio:
      <span class="primary--text ml-1">{{ buyOrder.fields.brokerageOther }}</span><br>
      5.4 Jos toimeksiantaja ei ole velvollinen maksamaan välityspalkkiota, toimeksiantaja
      suorittaa välitysliikkeelle kuitenkin seuraavat korvaukset:
      <span class="primary--text ml-1">{{ buyOrder.fields.brokerageOtherFee }}</span><br>
      5.5 Välityspalkkion perusteena on myyjän hyväksymässä ostotarjouksessa oleva tai
      kauppakirjassa sovittu (mikäli se poikkeaa ostotarjouksesta):
      <span v-if="buyOrder.fields.comissionBasis === 0" class="primary--text ml-1">
        Velaton kauppahinta</span>
      <span v-if="buyOrder.fields.comissionBasis === 1" class="primary--text ml-1">
        Kauppahinta</span>
    </p>
    <h2 class="mt-4">
      6. Toimeksiannon sisältö
    </h2>
    <p class="mt-4">
      6.1 Toimeksiantaja antaa välitysliikkeelle tehtäväksi etsiä toimeksiantajalle ostettavaksi
      edellä yksilöidyn huoneiston/kiinteistön:
      <span v-if="buyOrder.fields.exclusive === 0" class="primary--text ml-1">
        Yksinoikeudella</span>
      <span v-if="buyOrder.fields.exclusive === 1" class="primary--text ml-1">
        Ei yksinoikeudella</span>
    </p>
    <h2 class="mt-4">
      7. Muut ehdot
    </h2>
    <p class="mt-4">
      7.1 Toimeksiantaja on sitoutunut toimeksiantosopimuksen ehtoihin vasta
      välitysliikkeen toimitiloissa (ei kotimyyntitilanne):
      <span class="primary--text ml-1">
        {{ buyOrder.fields.condition1 ? 'Kyllä' : 'Ei' }}</span><br>
      7.2 Toimeksiantaja on sitoutunut toimeksiantosopimuksen ehtoihin muualla
      kuin välitysliikkeen toimitiloissa:
      <span class="primary--text ml-1">
        {{ buyOrder.fields.condition2 ? 'Kyllä' : 'Ei' }}</span><br>
      7.3 Toimeksiantaja kuittaa vastaanottaneensa erillisen peruuttamislomakkeen
      ja peruuttamisohjeen:
      <span class="primary--text ml-1">
        {{ buyOrder.fields.condition3 ? 'Kyllä' : 'Ei' }}</span><br>
      7.4 Toimeksiantaja pyytää, että välitysliike aloittaa toimeksiannon
      suorittamisen ennen kuin peruuttamisaika on päättynyt:
      <span class="primary--text ml-1">
        {{ buyOrder.fields.condition4 ? 'Kyllä' : 'Ei' }}</span><br>
      7.5 Annan suostumukseni sille, että toteutuneen kaupan tiedot julkistetaan
      ympäristöministeriön asuntojen.hintatiedot.fi -palvelussa:
      <span class="primary--text ml-1">
        {{ buyOrder.fields.condition5 ? 'Kyllä' : 'Ei' }}</span><br>
      7.6 Toimeksiannon muut ehdot:
      <span class="primary--text ml-1">
        {{ buyOrder.fields.otherConditions }}</span>
    </p>
    <h2 class="mt-4">
      8. Tietosuoja
    </h2>
    <p class="mt-4">
      8.1 Asiakasta on ohjattu tutustumaan etukäteen tietosuojaselosteeseen:
      <span class="primary--text ml-1">
        {{ buyOrder.fields.dataProtection1 ? 'Kyllä' : 'Ei' }}</span><br>
      8.2 Kaupan tietoja luovutetaan Kiinteistönvälitysalan Keskusliitto (KVKL) ry:lle, jossa tiedot
      tallennetaan KVKL:n hintaseurantapalveluun (HSP), jonka tietosuojaseloste on saatavilla
      osoitteesta www.hintaseurantapalvelu.fi/tietosuoja. Lisäksi pyydämme suostumustanne alla
      selostettuja tarkoituksia varten. Annatte suostumuksenne lisäämällä rastin ruutuun. Teillä
      on oikeus peruuttaa suostumus milloin tahansa ilmoittamalla peruuttamisesta kirjallisesti
      tai sähköpostitse välitysliikkeelle:
      <span class="primary--text ml-1">
        {{ buyOrder.fields.dataProtection2 ? 'Kyllä' : 'Ei' }}</span><br>
      8.3 Annan suostumukseni siihen, että välitysliike saa lähettää minulle sähköistä
      suoramarkkinointia (esim. tekstiviesti, sähköposti).
      <span class="primary--text ml-1">
        {{ buyOrder.fields.dataProtection3 ? 'Kyllä' : 'Ei' }}</span><br>
      8.4 Annan suostumukseni siihen, että välitysliike lähettää minulle, kaupan toiselle
      osapuolelle, isännöitsijälle, asiaan osalliselle pankille ja/tai viranomaiselle
      sähköpostitse henkilötunnukseni
      sisältäviä asiakirjoja. Henkilötunnuksen jakaminen näiden tahojen kanssa on välttämätöntä
      tunnistamisen varmistamiseksi.
      <span class="primary--text ml-1">
        {{ buyOrder.fields.dataProtection4 ? 'Kyllä' : 'Ei' }}</span><br>
      8.5 Olen tietoinen siitä, että sähköpostia ei lähetetä suojatussa yhteydessä eikä salattuna:
      <span class="primary--text ml-1">
        {{ buyOrder.fields.dataProtection5 ? 'Kyllä' : 'Ei' }}</span><br>
    </p>
    <h2 class="mt-4">
      9. Poliittinen vaikutusvalta
    </h2>
    <p class="mt-4">
      9.1 Oletko tai oletko ollut viimeisen vuoden aikana poliittisesti vaikutusvaltainen henkilö
      (PEP) tai sellaisen henkilön perheenjäsen tai läheinen yhtiökumppani?
      <span v-if="buyOrder.fields.pep === 0" class="primary--text ml-1">
        Kyllä</span>
      <span v-if="buyOrder.fields.pep === 1" class="primary--text ml-1">
        Ei</span>
    </p>
    <p class="mt-4">
      9.2 Lisätietoja
      <span v-if="buyOrder.fields.pep === 0" class="primary--text ml-1">
        {{ buyOrder.fields.pepMoreInfo }}
      </span>
    </p>
    <h2 class="mt-4">
      10. Allekirjoitukset
    </h2>
    <div v-if="buyOrder.signatures && buyOrder.signatures.length > 0" class="mt-4">
      <p v-for="(signature, idx) in buyOrder.signatures" :key="idx">
        10.{{ idx + 1 }}
        <span class="primary--text ml-1">
          {{ signature.name }} - Allekirjoitettu sähköisesti
        </span>
      </p>
    </div>
    <p v-else class="mt-4">
      Ei allekirjoituksia
    </p>
    <div v-if="agent">
      <h2 class="mt-5">
        Peruuttamislomake
      </h2>
      <p class="mt-4">
        Sisältyy PDF-tulosteeseen:
        <span class="primary--text">
          {{ buyOrder.fields.canceling ? 'Kyllä' : 'Ei' }}
        </span>
      </p>
    </div>
    <template v-if="buyOrder.property && agent">
      <v-divider/>
      <v-list>
        <v-subheader>Kohdekortti:</v-subheader>
        <v-list-item @click="goToProperty()">
          <v-list-item-avatar size="80">
            <v-img v-if="buyOrder.propertyimage" :src="buyOrder.propertyimage"/>
            <v-icon
              v-else
              size="50"
              color="primary"
            >
              home
            </v-icon>
          </v-list-item-avatar>

          <v-list-item-content>
            <v-list-item-title class="primary--text">
              {{ buyOrder.property.address }}
              {{ buyOrder.property.apartmentNumber ? ` ${buyOrder.property.apartmentNumber}` : '' }}
            </v-list-item-title>
            <v-list-item-subtitle v-text="buyOrder.property.rooms"/>
          </v-list-item-content>
          <v-list-item-action>
            <v-list-item-icon>
              <v-icon color="primary">
                keyboard_arrow_right
              </v-icon>
            </v-list-item-icon>
          </v-list-item-action>
        </v-list-item>
      </v-list>
    </template>
  </div>
</template>
<script>
import moment from 'moment';
import { mapGetters, mapState } from 'vuex';

const pdfMake = require('pdfmake/build/pdfmake.js');
const pdfFonts = require('../assets/fonts.js');

export default {
  components: {
    'buy-order-edit': () => import('@/components/BuyOrderEdit'),
  },
  props: {
    buyOrder: {
      type: Object,
      default: () => {},
    },
    currentStatus: {
      type: String,
      default: () => null,
    },
  },
  data: () => ({
    clauses: {},
    logItems: [],
    property: {
      image: '',
      title: 'ASdf',
      subtitle: 'asdf',
    },
    propertyTypesArray: [
      {
        value: 0,
        text: 'Omakotitalo',
      },
      {
        value: 1,
        text: 'Rivitalo',
      },
      {
        value: 2,
        text: 'Kerrostalo',
      },
      {
        value: 3,
        text: 'Loma-asunto',
      },
      {
        value: 4,
        text: 'Tontti',
      },
      {
        value: 5,
        text: 'Liiketila',
      },
      {
        value: 6,
        text: 'Autotalli',
      },
      {
        value: 7,
        text: 'Varasto',
      },
      {
        value: 8,
        text: 'Paritalo',
      },
      {
        value: 9,
        text: 'Erillistalo',
      },
      {
        value: 10,
        text: 'Lomahuoneisto',
      },
      {
        value: 11,
        text: 'Loma-asunto ulkomailla',
      },
      {
        value: 12,
        text: 'Loma-osake',
      },
      {
        value: 13,
        text: 'Omakotitalotontti',
      },
      {
        value: 14,
        text: 'Vapaa-ajan tontti',
      },
      {
        value: 15,
        text: 'Rivitalotontti',
      },
      {
        value: 16,
        text: 'Autopaikka',
      },
      {
        value: 17,
        text: 'Maatila',
      },
      {
        value: 18,
        text: 'Metsätila',
      },
      {
        value: 19,
        text: 'Toimistotila',
      },
      {
        value: 20,
        text: 'Tuotantotila',
      },
      {
        value: 21,
        text: 'Puutalo-osake',
      },
      {
        value: 22,
        text: 'Luhtitalo',
      },
      {
        value: 23,
        text: 'Muu toimitila',
      },
      {
        value: 24,
        text: 'Muu',
      },
    ],
  }),
  computed: {
    ...mapState({
      agent: state => state.agent,
    }),
    ...mapGetters({
      userDetails: 'userDetails',
    }),
  },
  async mounted() {
    const getClauses = () => import('./commissionAgreement/data/clauses.property.js');
    const { default: clauses } = await getClauses();
    this.clauses = clauses;
  },
  methods: {
    goToProperty() {
      const url = `${this.userDetails.rootpath}/hallinta.php?kohdeid=${this.buyOrder.property.id}`;
      window.location.href = url;
    },
    getStatus(status) {
      const statuses = {
        3: 'Luonnos',
        1: 'Aktiivinen',
        2: 'Toteutunut',
        0: 'Päätetty',
      };
      return statuses[status];
    },
    checkBox(checked = ' ', title, span = 1, bottomBorder = true, noBorders = false, small = false, rowSpan = 1) {
      const box = {
        table: {
          widths: [small ? 4 : 8],
          body: [
            [
              {
                text: checked,
                fontSize: small ? 6 : 8,
                margin: [0.5, 0.5, 0, 0],
                border: [noBorders, noBorders, true, true],
              },
            ],
          ],
        },
        border: [false, false, false, false],
        layout: {
          hLineColor: () => '#888888',
          vLineColor: () => '#888888',
          hLineWidth: () => 0.2,
          vLineWidth: () => 0.2,
        },
        margin: [-4, -2, 0, 0],
      };

      const checkbox = {
        table: {
          widths: [small ? 4 : 8, '*'],
          body: [
            [
              box,
              {
                text: title,
                fontSize: small ? 7 : 9,
                margin: [0, 0, 0, 0],
                border: [false, false, false, false],
                color: '#666666',
              },
            ],
          ],
        },
        margin: [-4, -2, 0, 5],
        colSpan: span,
        rowSpan,
        border: [!noBorders, !noBorders, !noBorders, bottomBorder],
        layout: {
          hLineColor: () => '#888888',
          vLineColor: () => '#888888',
          hLineWidth: () => 0.2,
          vLineWidth: () => 0.2,
        },
      };

      return checkbox;
    },
    clause(value, colSpan = 1) {
      return {
        text: value,
        fontSize: 7,
        margin: [0, 4, 0, 0],
        color: '#777777',
        colSpan,
      };
    },
    title(title) {
      return {
        text: title,
        margin: [0, 8, 0, 2],
        fontSize: 8.5,
        color: '#777777',
      };
    },
    pdfTable(title, value, colSpan = 1, rowSpan = 1, small = false) {
      return {
        table: {
          widths: ['*'],
          body: [
            [{
              text: title,
              fontSize: 7,
              margin: [0, 0, 0, 0],
              color: '#666666',
            }],
            [{ text: value, fontSize: small ? 5.5 : 9, margin: [0, -3, 0, 0] }],
          ],
        },
        rowSpan,
        colSpan,
        layout: 'noBorders',
        margin: [0, -2, 0, -2],
      };
    },
    printPdf() {
      pdfMake.vfs = pdfFonts.pdfMake.vfs;
      pdfMake.fonts = {
        Roboto: {
          normal: 'Roboto-Regular.ttf',
          bold: 'Roboto-Medium.ttf',
          italics: 'Roboto-Italic.ttf',
          bolditalics: 'Roboto-MediumItalic.ttf',
        },
        Icons: {
          normal: 'Icons.ttf',
        },
      };
      const line = '#888888';
      const greyBorders = () => ({
        hLineColor: () => line,
        vLineColor: () => line,
        hLineWidth: () => 0.2,
        vLineWidth: () => 0.2,
      });
      const content = [];
      if (this.buyOrder.logo) {
        content.push({
          image: this.buyOrder.logo, width: 300, alignment: 'center', margin: [0, 180, 0, 20],
        });
      }
      content.push({
        text: 'OSTOTOIMEKSIANTO', fontSize: 30, alignment: 'center', pageBreak: 'after',
      });
      // PRIVACY POLICY
      content.push({
        columns: [
          {
            color: '#666666',
            text: [
              { text: 'TIETOSUOJASELOSTE', fontSize: 7 },
              '\n\nTietosuojalain ja Euroopan Unionin yleisen tietosuoja-asetuksen (2016/679/EU) mukainen yhdistetty tietosuojaseloste ja informointiasiakirja.\n\n',
              { text: `1. REKISTERINPITÄJÄ\n${this.buyOrder.company.nimi}\n${this.buyOrder.company.osoite}\nY-tunnus: ${this.buyOrder.company.ytunnus}` },
              '\n\n2. HENKILÖTIETOJEN KÄSITTELIJÄ\nRMR Holding Oy / Helmi-kiinteistönvälitysjärjestelmä\nKauppakatu 7\n26100 Rauma\ny-tunnus: 2402115-6',
              '\n\n3. YHTEYDENOTTO TIETOSUOJA-ASIOISSA\ntekniikka@rmr.fi\n\n',
              this.clauses.privacyPolicy,
            ],
            fontSize: 5.5,
            width: '50%',
            margin: [13, 10, 2, 0],
          }, {
            color: '#666666',
            text: this.clauses.privacyPolicy2,
            fontSize: 5.5,
            width: '50%',
            margin: [2, 10, 13, 0],
          },
        ],
        pageBreak: 'after',
      });
      content.push({
        text: 'OSTOTOIMEKSIANTO',
        fontSize: 18,
        margin: [0, 20, 0, 0],
        color: '#333333',
      });
      content.push(this.title('1 TOIMEKSIANTAJAT'));
      const persons = {
        table: {
          widths: ['50%', '50%'],
          body: [
          ],
        },
        layout: greyBorders(),
      };
      let index = 1;
      this.buyOrder.persons.forEach((person) => {
        persons.table.body.push([
          this.pdfTable(`1.${index}.1 Nimi`, person.name),
          this.pdfTable(`1.${index}.2 Sähköposti`, person.email),
        ]);
        persons.table.body.push([
          this.pdfTable(`1.${index}.3 Henkilötunnus`, person.ssn),
          this.pdfTable(`1.${index}.4 Puhelinnumero`, person.phone),
        ]);
        persons.table.body.push([
          this.pdfTable(`1.${index}.5 Osoite`, person.address, 2),
        ]);
        persons.table.body.push([
          this.pdfTable(`1.${index}.6 Postinumero`, person.zip),
          this.pdfTable(`1.${index}.7 Kaupunki`, person.city),
        ]);
        index += 1;
      });
      if (this.buyOrder.persons.length > 0) {
        content.push(persons);
      }
      content.push(this.title('2 VÄLITYSLIIKE'));

      const agency = {
        table: {
          widths: ['33.3%', '33.3%', '33.4%'],
          body: [
          ],
        },
        layout: greyBorders(),
      };

      agency.table.body.push([
        this.pdfTable('2.1 Nimi', this.buyOrder.company.nimi, 1),
        this.pdfTable('2.2 Y-tunnus', this.buyOrder.company.ytunnus, 1),
        this.pdfTable('2.3 Osoite', this.buyOrder.company.osoite, 1),
      ]);
      agency.table.body.push([
        this.pdfTable('2.4 Toimeksiannon hoitaja', this.buyOrder.agent_details.nimi, 1),
        this.pdfTable('2.5 Puhelin', this.buyOrder.agent_details.puhelin, 1),
        this.pdfTable('2.6 Sähköposti', this.buyOrder.agent_details.email, 1),
      ]);

      content.push(agency);

      content.push(this.title('3 TOIMEKSIANNON KOHDE'));

      const summary = {
        table: {
          widths: ['50%', '50%'],
          body: [
          ],
        },
        layout: greyBorders(),
      };

      summary.table.body.push([
        this.pdfTable('3.1 Haluttu kohdetyyppi', this.buyOrder.readable_property_type, 2),
        '',
      ]);

      // summary.table.body.push([
      //   this.pdfTable('3.4 Julkinen kuvaus', this.buyOrder.description, 2),
      //   '',
      // ]);

      summary.table.body.push([
        this.pdfTable('3.2 Kohteen kunto', this.buyOrder.fields.condition, 2),
        '',
      ]);

      summary.table.body.push([
        this.pdfTable('3.3 Kohteen koko', this.buyOrder.fields.size, 2),
        '',
      ]);

      summary.table.body.push([
        this.pdfTable('3.4 Kohteen varustetaso', this.buyOrder.fields.equipment, 2),
        '',
      ]);

      summary.table.body.push([
        this.pdfTable('3.5 Kohteen vapautuminen', this.buyOrder.fields.vacancy, 2),
        '',
      ]);

      summary.table.body.push([
        this.pdfTable('3.6 Maksuehdot', this.buyOrder.fields.paymentTerms, 2),
        '',
      ]);

      summary.table.body.push([
        this.pdfTable('3.7 Kohteen muut ehdot ja erityistoiveet', this.buyOrder.fields.conditions, 2),
        '',
      ]);

      // Add these fields only to new buyorders that are created after this date
      if (moment(this.buyOrder.created, 'YYYY-MM-DD') > moment('2022-02-16')) {
        summary.table.body.push([
          this.pdfTable('3.8 Kohteen huonelukumäärä', this.buyOrder.fields.rooms, 2),
          '',
        ]);

        summary.table.body.push([
          this.pdfTable('3.9 Kohteen hinta', this.buyOrder.fields.price, 2),
          '',
        ]);
      }

      content.push(summary);

      content.push(this.title('4 VOIMASSAOLOAIKA'));

      const validity = {
        table: {
          widths: ['50%', '50%'],
          body: [
          ],
        },
        layout: greyBorders(),
      };

      validity.table.body.push([
        this.pdfTable('4.1 Toimeksiannon alku', this.buyOrder.fields.lengthPeriod !== 1 ? this.$options.filters.date(this.buyOrder.created) : ' '),
        this.pdfTable('4.2 Toimeksiannon loppu', this.buyOrder.fields.lengthPeriod !== 1 ? this.$options.filters.date(this.buyOrder.ends) : ' '),
      ]);

      validity.table.body.push([
        this.pdfTable('4.3 Toistaiseksi voimassa oleva alkaen', this.buyOrder.fields.lengthPeriod === 1 ? this.$options.filters.date(this.buyOrder.created) : ' ', 1),
        this.pdfTable('4.4 Toistaiseksi voimassa olevan toimeksiannon jakson pituus', this.buyOrder.fields.lengthPeriod === 1 ? this.buyOrder.fields.forNow : ' ', 1),
      ]);

      content.push(validity);
      content.push({
        text: 'Toistaiseksi voimassa oleva toimeksianto on voimassa toistaiseksi valitusta ajankohdasta alkaen kohdassa 4.4 valitun pituisissa jaksoissa niin, että se voidaan kirjallisesti irtisanoa viimeistään kymmenen (10) päivää ennen seuraavan sopimuskauden alkamista. Toimeksiannon voimassaolo päättyy kuitenkin aina neljän kuukauden kuluttua voimassaolon alkamisesta, ellei sitä kirjallisesti jatketa välityslain edellyttämällä tavalla. Neljän kuukauden enimmäisvoimassaoloaika ei koske sopimuksia, joissa toimeksiannon kohde hankitaan toimeksiantajan elinkeinotoimintaa varten.',
        fontSize: 7,
        margin: [0, 4, 0, 0],
        color: '#777777',
      });

      content.push(this.title('5 VÄLITYSPALKKIO'));

      const comission = {
        table: {
          widths: ['50%', '50%'],
          body: [
          ],
        },
        layout: greyBorders(),
      };

      comission.table.body.push([
        this.pdfTable('5.1 Välityspalkkio (kiinteä)', this.buyOrder.brokerage),
        this.pdfTable('5.2 Välityspalkkio (prosentti)', this.buyOrder.fields.brokeragePercent),
      ]);

      comission.table.body.push([
        this.pdfTable('5.3 Muu palkkioperuste, toimeksiantajalle aiheutuvat muut kustannukset ja vähimmäispalkkio', this.buyOrder.fields.brokerageOther, 2),
        '',
      ]);
      comission.table.body.push([
        this.pdfTable('5.4 Jos toimeksiantaja ei ole velvollinen maksamaan välityspalkkiota, toimeksiantaja suorittaa välitysliikkeelle kuitenkin seuraavat korvaukset', this.buyOrder.fields.brokerageOtherFee, 2),
        '',
      ]);

      const comissionBasis = {
        table: {
          widths: [100, 100, '*'],
          body: [
            [{
              text: '5.5 Välityspalkkion perusteena on myyjän hyväksymässä ostotarjouksessa oleva tai kauppakirjassa sovittu (mikäli se poikkeaa ostotarjouksesta)',
              fontSize: 7,
              margin: [-5, 3, 0, 2],
              color: '#666666',
              colSpan: 3,
            },
            '',
            '',
            ],
            [
              this.checkBox(this.buyOrder.fields.comissionBasis === 0 ? 'X' : ' ', 'Velaton kauppahinta', 1, true, true, true), // eslint-disable-line
              this.checkBox(this.buyOrder.fields.comissionBasis === 1 ? 'X' : ' ', 'Kauppahinta', 1, true, true, true), // eslint-disable-line
              ' ',
            ],
          ],
        },
        colSpan: 2,
        layout: 'noBorders',
        margin: [5, -2, 0, -5],
      };

      comission.table.body.push([comissionBasis, '']);

      content.push(comission);

      content.push(this.clause('Kaupanvahvistaja perii kaupanvahvistuksesta menevän maksun ja kulut kaupan osapuolilta. Toimeksiantaja vastaa sähköisessä kaupankäyntijärjestelmässä tehdyistä toimenpiteistä aiheutuvat kulut. '));

      content.push(this.title('6 TOIMEKSIANNON SISÄLTÖ'));

      const agreementContentWrap = {
        table: {
          widths: ['50%', '50%'],
          body: [
          ],
        },
        layout: greyBorders(),
      };

      const agreementContent = {
        table: {
          widths: [100, 100, '*'],
          body: [
            [{
              text: '6.1 Toimeksiantaja antaa välitysliikkeelle tehtäväksi etsiä toimeksiantajalle ostettavaksi edellä yksilöidyn huoneiston/kiinteistön',
              fontSize: 7,
              margin: [-5, 3, 0, 2],
              color: '#666666',
              colSpan: 3,
            },
            '',
            '',
            ],
            [
              this.checkBox(this.buyOrder.fields.exclusive === 0 ? 'X' : ' ', 'Yksinoikeudella', 1, true, true, true), // eslint-disable-line
              this.checkBox(this.buyOrder.fields.exclusive === 1 ? 'X' : ' ', 'Ei yksinoikeudella', 1, true, true, true), // eslint-disable-line
              ' ',
            ],
          ],
        },
        colSpan: 2,
        layout: 'noBorders',
        margin: [5, -2, 0, -5],
      };

      agreementContentWrap.table.body.push([agreementContent, '']);
      content.push(agreementContentWrap);

      content.push(this.clause(`6.2 Välitysliikkeen tehtävänä on:
- arvioida tarjottavan kohteen todennäköinen velaton kauppahinta kauppahintatilastojen ja/tai markkinatietämyksensä perusteella ja ilmoittaa
arvioinnin perusteet
- sopia toimeksiantajan valitsemiin kohteisiin tutustumisesta
- selvittää itsenäisen selonottovelvollisuutensa perusteella kohteen tiedot ja esittää ne toimeksiantajalle ennen ostotarjouksen tekemistä
- avustaa tarjousasiakirjojen laatimisessa
- kertoa toimeksiantajalle ja myyjälle tarjoukseen ja sen hyväksymiseen sekä mahdolliseen ehdollisuuteen liittyvistä kysymyksistä ottaen
huomioon molempien osapuolten edut
- laatia tai tarkistaa kauppakirja
- laatia varainsiirtoverolaskelma tai tarkistaa myyjän välitysliikkeen laatima varainsiirtoverolaskelma
- huolehtia kaupanteon käytännön järjestelyistä, ellei myyjän välitysliike huolehdi asiasta.

6.3 Välitysliikkeellä on oikeus välityspalkkioon, kun kaupasta on toimeksiannon voimassa ollessa sitovasti sovittu toimeksiantajan ja myyjän välilllä. Kiinteistön osalta sitova sopiminen edellyttää maakaaren 2 luvun 1 §:n mukaista muotoa.

6.4 Jos lopullista kauppaa ei synny myyjästä johtuvasta syystä ja sopimuksen peruuttamisen johdosta on sovittu maksettavaksi vakiokorvaus, välitysliikkeellä on oikeus puoleen toimeksiantajan saamasta vakiokorvauksesta. Välitysliikkeelle tuleva osuus voi kuitenkin olla enintään sovitun välityspalkkion suuruinen. Tästä huolimatta välitysliikkeellä on oikeus täysimääräiseen palkkioon saman tai jonkin toisen kaupan myöhemmin toteutuessa.

6.5 Jos toimeksiantaja toimeksiannon voimassaoloaikana muuten hankkii omistukseensa toimeksiannon kohteena olevan välitysliikkeen toimeksiantajalle tarjoaman huoneiston/kiinteistön tai sen osan, välitysliikkeellä on oikeus palkkioon ja kohdassa 5.3 sovittuihin kustannuksiin, jollei kohdassa 6.1. ole toisin sovittu.

6.6 Välitysliikkeellä on lisäksi oikeus palkkioon ja kohdassa 5.3 sovittuihin kustannuksiin, jos toimeksiantaja ei tee hyväksytyn ostotarjouksen mukaista kauppaa, kiinteistön osalta siitä riippumatta, onko tarjous tehty ja hyväksytty maakaaren 2 luvun 1 §:n edellyttämässä muodossa. Palkkio määräytyy kohdan 5 mukaisesti.

6.7 Mikäli välitysliikkeellä olevat osto- ja myyntitoimeksianto kohtaavat, välitysliikkeellä on oikeus vain yhden välityspalkkion määrään. Tällöin välitysliikkeellä on oikeus vain myyntitoimeksiannon mukaisen välityspalkkion määrään ja sovittuihin kustannuksiin, mitkä myyjä ja ostaja maksavat puoliksi.

6.8 Välityspalkkio ja muut toimeksiantajan maksettavaksi tulevat kustannukset erääntyvät maksettavaksi kaupantekotilaisuudessa. Jos välitysliikkeellä on oikeus palkkioon tai korvaukseen kohdissa 5 tai 6.3 - 6.5 määritellyillä perusteilla, palkkio tai korvaus erääntyy maksettavaksi 21 päivän kuluttua välitysliikkeen lähettämästä laskusta. Viivästyskorko on voimassa olevan korkolain mukainen.

6.9. Palkkio toimeksiantosopimuksen voimassaoloajan jälkeen
Välitysliikkeellä on oikeus palkkioon, jos välitysliikkeen toimeksiantajan ostettavaksi tarjoamaa huoneistoa/kiinteistöä tai sen osaa koskeva kauppa päätetään 6 kuukauden kuluessa tämän sopimuksen päättymisestä ja voidaan olettaa, että kaupan syntymiseen ovat olennaisesti vaikuttaneet välitysliikkeen toimenpiteet toimeksiantosopimuksen voimassaoloaikana.

6.10. Erimielisyyksien ratkaiseminen
Jos sopimusta koskevaa erimielisyyttä ei saada ratkaistuksi osapuolten välisillä neuvotteluilla, toimeksiantaja voi saattaa asian kuluttajariitalautakunnan (www.kuluttajariita.fi) ratkaistavaksi. Ennen asian viemistä kuluttajariitalautakunnan käsittelyyn toimeksiantajan tulee olla yhteydessä maistraattien kuluttajaneuvontaan (www.kuluttajaneuvonta.fi) Tätä sopimusta koskeva kanne on käsiteltävä toimeksiantajan Suomessa olevan vakituisen asuinpaikan tai kotipaikan kärjäjäoikeudessa. Mikäli kyseessä on kiinteistöä koskeva riita, voidaan kanne käsitellä kiinteistön sijaintipaikan käräjäoikeudessa. Jos toimeksiantajan asuinpaikka on ulkomailla, oikeuspaikkana on huoneiston tai kiinteistön sijaintipaikan käräjäoikeus. Toimeksiantajan suostumuksella välitysliikettä vastaan nostettava kanne voidaan käsitellä välitysliikkeen kotipaikan käräjäoikeudessa.

`));

      content.push(this.title('7 MUUT EHDOT'));

      const otherConditions = {
        table: {
          widths: ['50%', '50%'],
          body: [
            [
              this.checkBox(this.buyOrder.fields.condition1 ? 'X' : ' ', '7.1 Toimeksiantaja on sitoutunut toimeksiantosopimuksen ehtoihin vasta välitysliikkeen toimitiloissa (ei kotimyyntitilanne)', 2, true, false),
              '',
            ],
            [
              this.checkBox(this.buyOrder.fields.condition2 ? 'X' : ' ', '7.2 Toimeksiantaja on sitoutunut toimeksiantosopimuksen ehtoihin muualla kuin välitysliikkeen toimitiloissa', 2, true, false),
              '',
            ],
            [
              this.checkBox(this.buyOrder.fields.condition3 ? 'X' : ' ', '7.3 Toimeksiantaja kuittaa vastaanottaneensa erillisen peruuttamislomakkeen ja peruuttamisohjeen', 2, true, false),
              '',
            ],
            [
              this.checkBox(this.buyOrder.fields.condition4 ? 'X' : ' ', '7.4 Toimeksiantaja pyytää, että välitysliike aloittaa toimeksiannon suorittamisen ennen kuin peruuttamisaika on päättynyt', 2, true, false),
              '',
            ],
            [
              this.checkBox(this.buyOrder.fields.condition5 ? 'X' : ' ', '7.5 Annan suostumukseni sille, että toteutuneen kaupan tiedot julkistetaan ympäristöministeriön asuntojen.hintatiedot.fi -palvelussa', 2, true, false),
              '',
            ],
            [
              this.pdfTable('7.6 Toimeksiannon muut ehdot', this.buyOrder.fields.otherConditions || ' ', 2),
              '',
            ],
          ],
        },
        layout: greyBorders(),
      };

      content.push(otherConditions);
      content.push(
        this.clause('Mikäli jokin toimeksiantosopimuksen kohta on ristiriidassa tähän kohtaan "7.6 Toimeksiannon muut ehdot" kirjatun ehdon kanssa, ovat tähän kohtaan kirjatut ehdot etusijalla.'),
      );

      content.push(this.title('8 TIETOSUOJA'));
      content.push(this.clause(`Asiakas on tietoinen siitä, että tämän lomakkeen allekirjoittajan tietoja tallennetaan välitysliikkeen toimeksiantopäiväkirjaan ja asiakasrekisteriin kiinteistönvälitysliikkeistä ja vuokrahuoneiston välitysliikkeistä annetun lain sekä rahanpesun ja terrorismin rahoittamisen estämisestä annetun lain määräysten mukaisesti. Lisäksi henkilötietoja käytetään tietosuojaselosteessa tarkemmin kuvatun mukaisesti markkinointitarkoituksia ja liiketoiminnan kehittämistä varten.

`));
      const dataProtection = {
        table: {
          widths: ['50%', '50%'],
          body: [
            [
              this.checkBox(this.buyOrder.fields.dataProtection1 ? 'X' : ' ', '8.1 Asiakasta on ohjattu tutustumaan etukäteen tietosuojaselosteeseen.', 2, true, false),
              '',
            ],
            [
              this.checkBox(this.buyOrder.fields.dataProtection2 ? 'X' : ' ', '8.2 Kaupan tietoja luovutetaan Kiinteistönvälitysalan Keskusliitto (KVKL) ry:lle, jossa tiedot tallennetaan KVKL:n hintaseurantapalveluun (HSP), jonka tietosuojaseloste on saatavilla osoitteesta www.hintaseurantapalvelu.fi/tietosuoja. Lisäksi pyydämme suostumustanne alla selostettuja tarkoituksia varten. Annatte suostumuksenne lisäämällä rastin ruutuun. Teillä on oikeus peruuttaa suostumus milloin tahansa ilmoittamalla peruuttamisesta kirjallisesti tai sähköpostitse välitysliikkeelle.', 2, true, false),
              '',
            ],
            [
              this.checkBox(this.buyOrder.fields.dataProtection3 ? 'X' : ' ', '8.3 Annan suostumukseni siihen, että välitysliike saa lähettää minulle sähköistä suoramarkkinointia (esim. tekstiviesti, sähköposti).', 2, true, false),
              '',
            ],
            [
              this.checkBox(this.buyOrder.fields.dataProtection4 ? 'X' : ' ', '8.4 Annan suostumukseni siihen, että välitysliike lähettää minulle, kaupan toiselle osapuolelle, isännöitsijälle, asiaan osalliselle pankille ja/tai viranomaiselle sähköpostitse henkilötunnukseni sisältäviä asiakirjoja. Henkilötunnuksen jakaminen näiden tahojen kanssa on välttämätöntä tunnistamisen varmistamiseksi.', 2, true, false),
              '',
            ],
            [
              this.checkBox(this.buyOrder.fields.dataProtection5 ? 'X' : ' ', '8.5 Olen tietoinen siitä, että sähköpostia ei lähetetä suojatussa yhteydessä eikä salattuna.', 2, true, false),
              '',
            ],
          ],
        },
        layout: greyBorders(),
      };

      content.push(dataProtection);

      content.push(this.title('9 POLIITTINEN VAIKUTUSVALTA'));
      const pepWrap = {
        table: {
          dontBreakRows: true,
          unbreakable: true,
          widths: ['50%', '50%'],
          body: [
          ],
        },
        layout: greyBorders(),
      };

      const pep = {
        table: {
          widths: [100, 100, '*'],
          body: [
            [{
              text: '9.1 Oletko tai oletko ollut viimeisen vuoden aikana poliittisesti vaikutusvaltainen henkilö (PEP) tai sellaisen henkilön perheenjäsen tai läheinen yhtiökumppani?',
              fontSize: 7,
              margin: [-5, 3, 0, 2],
              color: '#666666',
              colSpan: 3,
            },
            '',
            '',
            ],
            [
              this.checkBox(this.buyOrder.fields.pep === 0 ? 'X' : ' ', 'Kyllä', 1, true, true, true), // eslint-disable-line
              this.checkBox(this.buyOrder.fields.pep === 1 ? 'X' : ' ', 'Ei', 1, true, true, true), // eslint-disable-line
              ' ',
            ],
            [
              this.pdfTable('9.2 Lisätietoja (PEP)', this.buyOrder.fields.pepMoreInfo || ' ', 3),
              '',
              '',
            ],
          ],
        },
        colSpan: 2,
        layout: 'noBorders',
        margin: [5, -2, 0, 5],
      };

      pepWrap.table.body.push([pep, '']);
      content.push(pepWrap);
      content.push(this.clause('\nAllekirjoittamalla tämän toimeksiantosopimuksen, osapuolet vakuuttavat, että tämän sopimuksen sisältö on käyty toimeksiantajan ja välitysliikkeen kesken läpi ennen sopimuksen allekirjoittamista. Tästä sopimuksesta liitteineen on laadittu samansisältöiset kappaleet toimeksiantajalle ja välitysliikkeelle.'));

      content.push(this.title('10 ALLEKIRJOITUKSET'));
      const chunk = (str, n) => {
        const ret = [];
        let idx;
        let len;

        for (idx = 0, len = str.length; idx < len; idx += n) {
          ret.push(str.substr(idx, n));
        }

        return ret;
      };
      let order = 1;
      if (this.buyOrder.signatures && this.buyOrder.signatures.length > 0) {
        const signatureTable = {
          table: {
            dontBreakRows: true,
            unbreakable: true,
            widths: ['*'],
            body: [],
          },
          margin: [0, 0, 0, 0],
          layout: greyBorders(),
        };
        this.buyOrder.signatures.forEach((signature) => {
          let sellerRow = ' ';
          sellerRow = [this.pdfTable(`10.${order} Allekirjoitus`, `${signature.name} - Allekirjoitettu sähköisesti`, 1)];
          signatureTable.table.body.push(sellerRow);
          const signatureDate = [this.pdfTable(`10.${order}.1 Allekirjoitusaika`, `${this.$options.filters.date(signature.created)} klo ${this.$options.filters.date(signature.created, { type: 'time' })}`)];
          signatureTable.table.body.push(signatureDate);
          const signatureRow = [this.pdfTable(`10.${order}.2 Sähköinen tunniste`, chunk(signature.signature, 135).join('\n'), 1, 1, 7)];
          signatureTable.table.body.push(signatureRow);
          order += 1;
        });
        content.push(signatureTable);
      } else {
        content.push('Ei allekirjoituksia');
      }

      const agentSign = {
        table: {
          widths: ['*'],
          body: [
            [
              this.pdfTable('11.1 Välittäjän allekirjoitus', `${this.buyOrder.agent_details.nimi} - Allekirjoitettu sähköisesti ${this.$options.filters.date(this.buyOrder.created)}` || ' ', 1),
            ],
          ],
        },
        layout: greyBorders(),
      };
      content.push(this.title('11. VÄLITYSLIIKKEEN ALLEKIRJOITUKSET'));
      content.push(agentSign);

      if (this.buyOrder.fields.canceling && this.buyOrder.fields.canceling.selectedAgent) {
        const agent = this.buyOrder.fields.canceling.selectedAgent;
        const agentDetails = `${agent.name}, ${agent.email}, ${agent.address}`;
        const cancelTable = {
          table: {
            dontBreakRows: true,
            widths: ['*'],
            body: [
              this.buyOrder.fields.canceling.tableRows[0],
              [{ columns: [this.pdfTable(`Peruuttaminen tulee osoittaa seuraavalle yrityksemme (${agent.companyName}) edustajalle`, agentDetails)], margin: [8, 8, 8, 8] }],
              this.buyOrder.fields.canceling.tableRows[1],
              this.buyOrder.fields.canceling.tableRows[2],
            ],
          },
          layout: greyBorders(),
          margin: [0, 8, 0, 8],
        };

        content.push({ text: ' ', pageBreak: 'before' });
        if (this.buyOrder.logo !== false) {
          content.push({
            image: this.buyOrder.logo,
            fit: [160, 50],
            width: 160,
            height: 50,
            margin: [0, 0, 0, 10],
          });
        }
        content.push('KOTIMYYNTISOPIMUS');
        content.push(cancelTable);
      }
      // TODO: 11. Välitysliikkeen allekirjoitukset

      const docDefinition = {
        content,
        footer: (currentPage, pageCount) => ({
          color: '#666666',
          text: `OSTOTOIMEKSIANTO #${this.buyOrder.ordernumber} - Sivu: ${currentPage.toString()} / ${pageCount}`,
          fontSize: 8,
          alignment: 'right',
          width: '50%',
          margin: [0, 2, 14, 4],
        }),
        header:
          {
            columns: [
              {
                color: '#666666',
                text: `OSTOTOIMEKSIANTO #${this.buyOrder.ordernumber}`,
                fontSize: 8,
                alignment: 'left',
                width: '50%',
                margin: [15, 15, 0, 0],
              }, {
                color: '#666666',
                text: this.buyOrder.company.nimi,
                fontSize: 8,
                alignment: 'right',
                width: '50%',
                margin: [0, 15, 15, 0],
              },
            ],
          },
        defaultStyle: {
          font: 'Roboto',
        },
        pageMargins: [15, 28, 15, 38],
        styles: {
          grayLines: {
            hLineColor: () => 'gray',
            vLineColor: () => 'gray',
          },
        },
      };
      pdfMake.createPdf(docDefinition).download('Ostotoimeksianto.pdf');
    },
  },
};
</script>
